const htmlContent = `
<div id="markdown">
<h1 id="g1-mail-a-small-container-native-backoffice-mail-service">g1-mail: a small, container-native backoffice mail service</h1>
<h2 id="overview">Overview</h2>
<p>g1-mail is IMAP and SMTP for a small setup, entirely in containers: a Dovecot container for mailboxes, a Postfix container for sending and receiving, and a getmail container that pulls in mail from elsewhere. No web admin console, no huge all-in-one mail image — just the pieces a small company or a home-office setup actually needs, each in its own small, rebuildable container.</p>
<h2 id="small-on-purpose">Small, on purpose</h2>
<p>Bigger mail-in-a-box projects bundle everything — webmail, antivirus, a full admin UI, a dozen protocols you&#39;ll never use. g1-mail bundles exactly three services because that&#39;s what an actual backoffice needs: somewhere to store mail (Dovecot), something to send and receive it (Postfix), and something to fetch it from external accounts into one place (getmail). Each is its own container with its own small Dockerfile, so any one of them can be rebuilt or replaced without touching the others.</p>
<h2 id="the-clever-part-how-the-ports-are-split">The clever part: how the ports are split</h2>
<p>The whole setup runs as one <code>docker-compose.yaml</code>, and what makes it work smoothly is treating each exposed port as a deliberate, minimal decision rather than opening everything by default:</p>
<ul>
<li><strong>Postfix only publishes port 25.</strong> That&#39;s the entire internet-facing surface of the stack — nothing else about it is reachable from outside.</li>
<li><strong>Dovecot publishes 143 and 993</strong> for mail clients to actually fetch mail (IMAP and IMAPS), plus <strong>port 24 for LMTP</strong> — the standard local-delivery protocol port. That&#39;s the internal handoff: Postfix, in its own separate container, delivers incoming mail to Dovecot over that LMTP port rather than needing a shared filesystem or socket between two containers that otherwise know nothing about each other.</li>
<li><strong>Outbound mail doesn&#39;t go out directly.</strong> Postfix is configured to relay outbound mail through an external provider&#39;s SMTP relay (credentials passed in as environment variables) instead of sending directly from the box&#39;s own IP. A small root server or home connection rarely has the sending reputation to land in inboxes reliably on its own — relaying through a provider that already has that reputation sidesteps the problem entirely.</li>
</ul>
<p>Two containers, talking to each other over a standard protocol port instead of container-internal tricks, is what keeps each piece independently simple — and it&#39;s the same server this relay setup ultimately talks to that the next post on this blog is about.</p>
<h2 id="the-compose-file">The compose file</h2>
<pre><code class="language-yaml">services:
  dovecot:
    profiles: [&quot;lyra&quot;, &quot;gpi06&quot;]
    build: ./dovecot/
    restart: &#39;always&#39;
    hostname: &#39;g1-dovecot&#39;
    container_name: g1-dovecot
    ports:
      - &quot;143:143&quot;
      - &quot;993:993&quot;
      - &quot;24:24&quot;
    volumes:
      - /mail/:/home/
      - ./dovecot/etc/users:/etc/dovecot/users
    cap_add: [ &quot;NET_ADMIN&quot;, &quot;SYS_PTRACE&quot; ]
    healthcheck:
      test: [&quot;CMD-SHELL&quot;, &quot;nc -z localhost 143&quot;]
      interval: 30s
      timeout: 5s
      retries: 3
      start_period: 10s

  postfix:
    profiles: [&quot;lyra&quot;, &quot;gpi06&quot;]
    build: ./postfix/
    restart: unless-stopped
    hostname: &#39;g1-postfix&#39;
    container_name: g1-postfix
    ports:
      - &quot;25:25&quot;
    environment:
      - SYSTEM_TIMEZONE=&quot;Europe/Berlin&quot;
      - _IONOS1_RELAY_USERNAME=\${_IONOS1_RELAY_USERNAME}
      - _IONOS1_RELAY_PASSWORD=\${_IONOS1_RELAY_PASSWORD}
      - _1BLU1_RELAY_USERNAME=\${_1BLU1_RELAY_USERNAME}
      - _1BLU1_RELAY_PASSWORD=\${_1BLU1_RELAY_PASSWORD}
    healthcheck:
      test: [&quot;CMD-SHELL&quot;, &quot;postfix status &gt; /dev/null 2&gt;&amp;1&quot;]
      interval: 30s
      timeout: 5s
      retries: 3
      start_period: 10s
    depends_on:
      dovecot:
        condition: service_healthy

  getmail:
    profiles: [&quot;lyra&quot;]
    build: ./getmail/
    container_name: g1-getmail
    restart: unless-stopped
    volumes:
      - ./getmail/data/:/var/lib/getmail/
      - ./getmail/rc.d/:/var/lib/getmail/rc.d/
      - ./getmail/logs/:/var/log/getmail/
    depends_on:
      dovecot:
        condition: service_healthy
      postfix:
        condition: service_healthy
</code></pre>
<p>Two details worth noticing beyond the ports: <code>postfix</code> declares <code>depends_on: dovecot: condition: service_healthy</code>, so it won&#39;t even start accepting mail until Dovecot&#39;s healthcheck passes — no window where Postfix is up but has nowhere to deliver to. And <code>getmail</code> only carries the <code>lyra</code> profile, not <code>gpi06</code>, so on a setup running both compose files redundantly, only one of them actually polls external accounts — the other stays a pure relay/mailbox pair, avoiding two containers fetching (and duplicating) the same external mail.</p>

</div>
`;
export default htmlContent
